<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import find from 'lodash-es/find';
import isEqual from 'lodash-es/isEqual';

import Icon from '@/components/icon/Icon.vue';
import CloseX from '@/icons/close-x.svg';
import Caret from '@/icons/caret.svg';

/**
 * Options attribute needs this format :
 * [
 *   {
 *      id: 87,
 *      title: "un titre"
 *   },
 *   {
 *      id: 88,
 *      title: "un titre 2"
 *   }
 * ]
 */
const props = defineProps({
    options: {
        type: Array,
        required: true,
    },
    defaultValue: {
        type: [String, Object],
        required: false,
        default: null,
    },
    tabindex: {
        type: Number,
        required: false,
        default: 0,
    },
    preselectedId: {
        type: String,
        required: false,
        default: '',
    },
    preselectedSlug: {
        type: String,
        required: false,
        default: '',
    },
    colors: {
        type: Object,
        required: false,
        default: () => ({
            bg: 'dark-blue',
            text: 'white',
        }),
    },
    side: {
        type: String,
        default: 'left',
    },
    resetAfterSelect: {
        type: Boolean,
        default: false,
    },
    canClear: {
        type: Boolean,
        default: true,
    },
});
const emit = defineEmits(['input', 'initialInput']);
const selected = ref(null);
const button = ref(null);
const rootEl = ref(null);
const wrapper = ref(null);
// console.log(props.defaultValue, props.options[0]);
selected.value = props.defaultValue ? props.defaultValue : props.options.length > 0 ? props.options[0] : null;
const open = ref(false);
const getIconName = (option) => {
    if (!option.icon) {
        return '';
    }

    if (option.iconSuffix) {
        return option.slug + option.iconSuffix;
    }

    return option.slug;
};

watch(props, () => {
    if (props.defaultValue) {
        selected.value = props.defaultValue;
    }
});

const selectOption = (option) => {
    selected.value = option;
    open.value = false;
    emit('input', option);

    if (props.resetAfterSelect) {
        selectOption(props.options[0]);
    }
};
const wrapperLarger = ref(true);
const wrapperLargerOrEqual = ref(true);
watch(open, () => {
    nextTick(() => {
        if (open.value) {
            wrapperLarger.value = wrapper.value.$el.getBoundingClientRect().width > button.value.getBoundingClientRect().width;
            wrapperLargerOrEqual.value = wrapper.value.$el.getBoundingClientRect().width >= button.value.getBoundingClientRect().width;
        } else {
            wrapperLarger.value = true;
            wrapperLargerOrEqual.value = true;
        }
    });
});

onMounted(() => {
    if (props.preselectedId) {
        const foundOption = find(props.options, (opt) => opt.id === props.preselectedId);
        if (foundOption) {
            selected.value = foundOption;
        }
    } else if (props.preselectedSlug) {
        const foundOption = find(props.options, (opt) => opt.slug === props.preselectedSlug);
        if (foundOption) {
            selected.value = foundOption;
        }
    }

    emit('initialInput', selected.value);
});
</script>

<template>
    <div
        ref="rootEl"
        v-click-outside="
            () => {
                open = false;
            }
        "
        class="CustomSelect relative inline-flex h-12 text-left leading-none outline-none"
        :tabindex="tabindex"
    >
        <div
            ref="button"
            class="CustomSelect__selected align-center relative z-2 flex h-full w-full cursor-pointer items-center font-medium transition-all duration-500 md:w-auto"
            :class="[
                'bg-' + colors.bg,
                'text-' + colors.text,
                { open: open, 'rounded-xl': !open, 'rounded-t-xl': open, 'rounded-bl-xl': !wrapperLargerOrEqual },
            ]"
        >
            <span
                class="CustomSelect__selected-title flex h-full w-full items-center truncate whitespace-nowrap pl-5 pr-3 hover:bg-white/10 md:w-auto"
                v-on:click="open = !open"
            >
                <span>{{ selected.title }}</span>
            </span>

            <button
                v-if="!canClear || isEqual(selected, options[0]) || open"
                class="CustomSelect__icon flex h-full w-8 items-center justify-center pr-1 hover:bg-white/10"
                v-on:click="open = !open"
            >
                <Caret
                    class="CustomSelect__arrow pointer-events-none h-auto w-1.5 fill-current transition"
                    :class="['text-' + colors.text, { '-rotate-90': open, 'rotate-90': !open }]"
                />
            </button>

            <button
                v-else
                class="CustomSelect__icon-close flex h-full w-8 items-center justify-center pr-1 hover:bg-white/10"
                v-on:click="selectOption(options[0])"
            >
                <CloseX
                    class="CustomSelect__arrow pointer-events-none h-auto w-2 fill-current transition"
                    :class="['text-' + colors.text]"
                />
            </button>
        </div>

        <transition
            enter-active-class="transition-[max-height,border-radius] duration-500"
            leave-active-class="transition-[max-height,border-radius] duration-500"
            enter-from-class="!max-h-0"
            enter-to-class="!max-h-59"
            leave-from-class="!max-h-59"
            leave-to-class="!max-h-0"
            mode="out-in"
        >
            <transition-group
                v-show="open"
                ref="wrapper"
                class="CustomSelect__items absolute top-full z-1 max-h-59 w-fit min-w-full overflow-scroll rounded-b-xl"
                :class="[
                    'bg-' + colors.bg,
                    'text-' + colors.text,
                    {
                        'rounded-tl-xl': wrapperLarger && side == 'right',
                        'rounded-tr-xl': wrapperLarger && side == 'left',
                        'left-0': side === 'left',
                        'right-0': side === 'right',
                    },
                ]"
                tag="div"
                enter-active-class="transition duration-500"
                leave-active-class="transition duration-500"
                enter-from-class="translate-x-2 opacity-0"
                enter-to-class="translate-x-0 opacity-100"
                leave-from-class="translate-x-0 opacity-100"
                leave-to-class="translate-x-2 opacity-0"
                mode="out-in"
                v-on:wheel.stop
                v-on:scroll.stop
            >
                <div
                    v-for="(option, i) of options"
                    v-show="open"
                    :key="i + option.title"
                    class="CustomSelect__item-wrapper flex cursor-pointer select-none items-baseline whitespace-nowrap px-4 leading-[2.25] transition hover:bg-white/25"
                    :class="['text-' + colors.text, { 'bg-white/15': isEqual(selected, option) }]"
                    :style="'transition-delay:' + Math.min(i * 25, 250) + 'ms'"
                    v-on:click="selectOption(option)"
                >
                    <div
                        v-if="option.icon"
                        class="CustomSelect__item__icon-wrapper relative w-8"
                    >
                        <div class="absolute left-[2px] top-[-5px] z-0 h-[1.35rem] w-[1.35rem] rounded-[100%] bg-white" />
                        <Icon
                            class="CustomSelect__item-icon relative z-[1] mr-2 h-[0.85em] w-auto"
                            :type="getIconName(option)"
                        />
                    </div>
                    {{ option.title }}
                </div>
            </transition-group>
        </transition>
    </div>
</template>
